"use client";

import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api-client";

/**
 * How many ToniAgent-team feedback reports are still open, across every clinic.
 *
 * Lives in a context rather than in the sidebar because the count has two
 * writers — the `/admin/feedback` queue and the per-call `CallTeamFeedbackCard` —
 * and one reader, the sidebar badge, which stays mounted across client-side
 * navigation. Without a shared refresh, closing the whole queue would leave a
 * stale nonzero badge for the rest of the session. Mirrors what
 * `useMenuData().refreshMenuData()` does for the clinic's `pending_tasks_count`.
 *
 * Admin-only: for everyone else the count stays 0 and no request is made (the
 * endpoint 403s for them).
 */

interface TeamFeedbackContextType {
  openCount: number;
  refreshOpenCount: () => Promise<void>;
}

const TeamFeedbackContext = createContext<TeamFeedbackContextType | undefined>(undefined);

export function TeamFeedbackProvider({ children }: { children: ReactNode }) {
  const { isAdmin, loading: authLoading } = useAuth();
  // The boolean, not `isAdmin` itself — the hook's identity changes with `user`,
  // and depending on the function would re-create the callback needlessly.
  const admin = isAdmin();

  const [openCount, setOpenCount] = useState(0);

  const refreshOpenCount = useCallback(async () => {
    if (!admin) {
      setOpenCount(0);
      return;
    }
    try {
      setOpenCount(await api.countPendingAgentTeamTasks());
    } catch {
      // A failed count must never break the sidebar; showing no badge is the
      // safe degradation.
      setOpenCount(0);
    }
  }, [admin]);

  useEffect(() => {
    if (authLoading) return;
    void refreshOpenCount();
  }, [authLoading, refreshOpenCount]);

  return (
    <TeamFeedbackContext.Provider value={{ openCount, refreshOpenCount }}>
      {children}
    </TeamFeedbackContext.Provider>
  );
}

export function useTeamFeedback() {
  const context = useContext(TeamFeedbackContext);
  if (context === undefined) {
    throw new Error("useTeamFeedback must be used within a TeamFeedbackProvider");
  }
  return context;
}
